import { useMedia } from '@/hooks/useMedia/useMedia';
import { Classroom } from '@/types/classroom';
import { SharableClassroomLink } from '@/utils/link';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LanIcon from '@mui/icons-material/Lan';
import ShareIcon from '@mui/icons-material/Share';
import { Box, Button, IconButton, Popover, Stack, Typography } from '@mui/material';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ClassroomSettings } from '../../../types/session';
import { ClassroomMenus, MenusOption } from './ClassroomWritingMenus';

type ClassroomPageHeaderProps = {
    classroom?: Classroom;
    settings?: ClassroomSettings;
    onEndClass?: (classroom: Classroom) => Promise<void>;
    onSettingsChange?: (settings: ClassroomSettings) => void;
    onReassignStudents?: () => Promise<void>;
};

export function ClassroomPageHeader({ classroom, settings, onEndClass, onSettingsChange, onReassignStudents }: ClassroomPageHeaderProps) {
    const { t } = useTranslation();
    const { isMobile } = useMedia();

    const [terminating, setTerminating] = React.useState(false);
    const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);

    const handleInfoClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleInfoClose = () => {
        setAnchorEl(null);
    };

    const handleSettingToggle = useCallback(
        (option: MenusOption, checked: boolean) => {
            if (!onSettingsChange || !settings) return;

            switch (option) {
                case 'strokeOrder':
                    onSettingsChange({ ...settings, freeStyleMode: checked });
                    break;
                case 'copybook':
                    onSettingsChange({ ...settings, writingHints: checked });
                    break;
                default:
                    break;
            }
        },
        [onSettingsChange, settings],
    );

    return (
        <Box
            sx={(theme) => ({
                width: '100%',
                px: 2,
                py: 1,
                ...theme.applyStyles('dark', { backgroundColor: (theme.vars || theme).palette.grey[800] }),
            })}
        >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1, alignItems: 'center' }}>
                <Stack direction='row' spacing={1} sx={{ alignItems: 'center' }}>
                    <Typography variant='h5' noWrap>
                        {t('classroom.label.otp').toLocaleUpperCase()}: {classroom?.otp}
                    </Typography>
                    <IconButton aria-label='info' onClick={handleInfoClick} size='small'>
                        <InfoOutlinedIcon fontSize='small' />
                    </IconButton>
                    <Popover
                        open={Boolean(anchorEl)}
                        anchorEl={anchorEl}
                        onClose={handleInfoClose}
                        anchorOrigin={{
                            vertical: 'top',
                            horizontal: 'right',
                        }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: -8, // 8px from the right
                        }}
                        slotProps={{
                            paper: { sx: { p: 2 } },
                        }}
                    >
                        <Stack spacing={1}>
                            <Stack direction='row' spacing={1} alignItems='flex-end'>
                                <Typography variant='caption'>
                                    {t('classroom.label.classroomName')}
                                    <Typography variant='h6' color='primary'>
                                        {classroom?.testroomName}
                                    </Typography>
                                </Typography>
                                {import.meta.env.MODE !== 'production' && classroom?.id && navigator.canShare && navigator.canShare({ url: SharableClassroomLink(classroom.id) }) && (
                                    <IconButton
                                        aria-label='share'
                                        size='small'
                                        onClick={() => {
                                            navigator.share({ url: SharableClassroomLink(classroom.id) });
                                        }}
                                    >
                                        <ShareIcon fontSize='small' />
                                    </IconButton>
                                )}
                            </Stack>
                            <Typography variant='caption'>
                                {t('classroom.label.lessonName').toLocaleUpperCase()}
                                <Typography variant='h6'>{classroom?.lessonVersion.lessonName}</Typography>
                            </Typography>
                            {classroom?.passlock && (
                                <Typography variant='caption'>
                                    {t('lesson.label.parentalLock').toLocaleUpperCase()}
                                    <Typography variant='h6' color='secondary'>
                                        {classroom.passlock}
                                    </Typography>
                                </Typography>
                            )}
                        </Stack>
                    </Popover>
                </Stack>
                <Stack direction='row' spacing={1}>
                    <Button
                        variant='contained'
                        color='error'
                        startIcon={isMobile ? undefined : <CloseIcon />}
                        loading={terminating}
                        onClick={async () => {
                            if (classroom && onEndClass) {
                                setTerminating(true);
                                await onEndClass(classroom);
                                setTerminating(false);
                            }
                        }}
                    >
                        {isMobile ? <CloseIcon /> : t('classroom.actions.endClass')}
                    </Button>
                </Stack>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Button variant='outlined' startIcon={<LanIcon />} onClick={onReassignStudents}>
                    {t('classroom.actions.reassignStudents')}
                </Button>
                <ClassroomMenus title={t('classroom.actions.writing')} value={settings} onSettingToggle={handleSettingToggle} />
            </Box>
        </Box>
    );
}
